// src/pages/SchoolDashboard.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  collection,
  collectionGroup,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
  addDoc,
  setDoc,
  deleteDoc,
  serverTimestamp, // ✅ use server timestamps
  getDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../App.css";
import { signOut } from "firebase/auth";
import { Tabs, Tab, Modal, Box, Tooltip, Button, useMediaQuery } from "@mui/material";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { db, auth as sharedAuth } from "../firebase";


/* ----------------------------- Small utilities ----------------------------- */
const ALLOWED_ROLES = new Set(["school_admin", "teacher", "admin"]);
const functions = getFunctions(undefined, "us-central1");
const PER_STUDENT_CAP = 40; // global cap (school edition)

function asDateMaybe(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v > 1e12 ? v : v * 1000);
  if (v?.toMillis) { try { return new Date(v.toMillis()); } catch {} }
  if (v?._seconds != null) return new Date(v._seconds * 1000);
  if (v?.seconds != null) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}
function msFromAny(v) {
  if (!v) return null;
  if (typeof v === "number") return v > 1e12 ? v : v * 1000;
  if (v?.toMillis) { try { return v.toMillis(); } catch {} }
  if (v?._seconds != null) return v._seconds * 1000;
  if (v?.seconds != null) return v.seconds * 1000;
  const d = new Date(v);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}
function formatDate(d) {
  if (!d) return "N/A";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function normalizeBilling(interval) {
  const iv = String(interval || "").toLowerCase();
  if (["year", "annual", "yearly"].includes(iv)) return "annual";
  if (["month", "monthly"].includes(iv)) return "monthly";
  return "—";
}
function normalizeReadingActivity(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return { labels: [], stories: [], minutes: [] };
  const items = raw.map((it) => {
    if (typeof it?.t === "number" && typeof it?.v === "number") {
      return { date: new Date(it.t), stories: 1, minutes: Math.max(0, Number(it?.v) || 0) };
    }
    const d = asDateMaybe(it?.date) || new Date();
    const minutes = Math.round((Number(it?.timeSpent) || 0) / 60);
    const stories = Number(it?.storiesGenerated) || 0;
    return { date: d, stories, minutes: Math.max(0, minutes) };
  });
  const byDay = new Map();
  items.forEach(({ date, stories, minutes }) => {
    const label = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const prev = byDay.get(label) || { stories: 0, minutes: 0 };
    byDay.set(label, { stories: prev.stories + stories, minutes: prev.minutes + minutes });
  });
  const labels = Array.from(byDay.keys());
  const slice = labels.slice(-7);
  return {
    labels: slice,
    stories: slice.map((l) => byDay.get(l).stories),
    minutes: slice.map((l) => byDay.get(l).minutes),
  };
}
function normalizeGenres(genres) {
  if (!genres) return { labels: [], counts: [] };
  if (Array.isArray(genres)) {
    const counts = {};
    genres.forEach((g) => {
      const k = String(g || "").trim();
      if (!k) return;
      counts[k] = (counts[k] || 0) + 1;
    });
    const labels = Object.keys(counts);
    return { labels, counts: labels.map((k) => counts[k]) };
  }
  if (typeof genres === "object") {
    const labels = Object.keys(genres);
    return { labels, counts: labels.map((k) => Number(genres[k]) || 0) };
  }
  return { labels: [], counts: [] };
}



/* ------ calendar helpers for safe fallback ------ */
function startOfCalendarMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}
function endOfCalendarMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0, 0); // exclusive
}

function parseActivityDate(dateStrOrObj) {
  if (!dateStrOrObj) return null;

  // If it's exactly YYYY-MM-DD, treat it as end-of-day LOCAL time
  // so it reliably falls inside endExclusive monthly windows.
  if (typeof dateStrOrObj === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStrOrObj)) {
    const [y, m, d] = dateStrOrObj.split("-").map(Number);
    return new Date(y, m - 1, d, 23, 59, 59, 999);
  }

  return typeof dateStrOrObj === "string"
    ? new Date(dateStrOrObj)
    : asDateMaybe(dateStrOrObj);
}


function inRange(dateStrOrObj, start, endExclusive) {
  const d = parseActivityDate(dateStrOrObj);
  if (!d) return false;
  if (start && d < start) return false;
  if (endExclusive && d >= endExclusive) return false;
  return true;
}
function sumStoriesInRange(activityArr, start, endExclusive) {
  const arr = Array.isArray(activityArr) ? activityArr : [];
  return arr.reduce((sum, e) => {
    const when = e?.date || e?.ts || e?.time || null;
    const stories = Number(e?.storiesGenerated || 0);
    if (!start && !endExclusive) return sum + stories;
    return inRange(when, start, endExclusive) ? sum + stories : sum;
  }, 0);
}

function addMonths(date, months) {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() !== day) d.setDate(0);
  return d;
}

function getMonthlyWindowAnchored(anchorStart, now = new Date()) {
  const start = new Date(anchorStart);

  let months =
    (now.getFullYear() - start.getFullYear()) * 12 +
    (now.getMonth() - start.getMonth());

  let windowStart = addMonths(start, months);
  let windowEnd = addMonths(windowStart, 1);

  while (now < windowStart) {
    months -= 1;
    windowStart = addMonths(start, months);
    windowEnd = addMonths(windowStart, 1);
  }
  while (now >= windowEnd) {
    windowStart = windowEnd;
    windowEnd = addMonths(windowStart, 1);
  }

  return { start: windowStart, endExclusive: windowEnd };
}



/* ------------------------------ Main Component ---------------------------- */
const SchoolDashboard = ({ overrideSchoolId = null, adminView = false }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const loc = useLocation();


  // identity
  const [currentUser, setCurrentUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  // org/school
  const [schoolId, setSchoolId] = useState(
  overrideSchoolId || localStorage.getItem("schoolId") || ""
);
  const [resolvingSchool, setResolvingSchool] = useState(true);
  const [noSchool, setNoSchool] = useState(false);

  // core docs/lists
  const [schoolDoc, setSchoolDoc] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [themes, setThemes] = useState([]); // [{ id, ref, ...data }]

  // listener error flags
  const [permErrors, setPermErrors] = useState({ teachers: false, students: false, themes: false });
  const [themesIndexHint, setThemesIndexHint] = useState(""); // capture failed-precondition details

  // UI State
  // eslint-disable-next-line no-unused-vars
  const thead = null;

  const [activeTab, setActiveTab] = useState(0);
  const [diversityMode, setDiversityMode] = useState(false);
  const [filterClass, setFilterClass] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [guideExpanded, setGuideExpanded] = useState(false);

  // Student modal
  const [openStudentModal, setOpenStudentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Theme editor
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [themeSetting, setThemeSetting] = useState("");
  const [themeDescription, setThemeDescription] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [themeComment, setThemeComment] = useState("");
  const [editThemeData, setEditThemeData] = useState(null); // { id, ref, ...data }

  // Billing UI
const [openingPortal, setOpeningPortal] = useState(false);
const [renewing, setRenewing] = useState(false); // 🔹 NEW: renewal in progress


  /* ----------------------------- Auth + user doc ---------------------------- */
  useEffect(() => {
    const off = sharedAuth.onAuthStateChanged((u) => {
      setCurrentUser(u || null);
      if (!u) {
        setUserDoc(null);
        setUserLoading(false);
        setResolvingSchool(false);
        return;
      }
      const uref = doc(db, "users", u.uid);
      const unsub = onSnapshot(
        uref,
        (snap) => {
          setUserDoc(snap.exists() ? snap.data() : null);
          setUserLoading(false);
        },
        () => setUserLoading(false)
      );
      return () => unsub();
    });
    return () => off();
  }, []);

  // mark last dashboard as school for all fallbacks
  useEffect(() => {
    localStorage.setItem("lastDashboard", "/school-dashboard");
  }, []);


  // Ensure schoolId (prefer override, then localStorage, else user doc)
useEffect(() => {
  // If admin is explicitly viewing a particular school, don't overwrite it
  if (overrideSchoolId) {
    setResolvingSchool(false);
    setNoSchool(false);
    return;
  }

  if (!currentUser) return;

  if (schoolId) {
    setResolvingSchool(false);
    return;
  }

  if (!userLoading) {
    const sid = userDoc?.orgId || userDoc?.schoolId || "";
    if (sid) {
      localStorage.setItem("schoolId", sid);
      setSchoolId(sid);
      setNoSchool(false);
    } else {
      setNoSchool(true);
    }
    setResolvingSchool(false);
  }
}, [currentUser, userLoading, userDoc, schoolId, overrideSchoolId]);


  /* --------------------------- Permission calculation ---------------------- */
  const canAdmin = useMemo(() => {
  if (!currentUser || !schoolId) return false;

  // 👇 In adminView, we trust Firestore rules and allow the dashboard to load
  if (adminView) return true;

  const role = String(userDoc?.role || "").toLowerCase();
  if (!ALLOWED_ROLES.has(role)) return false;

  // Prefer schoolId, then orgId. Must equal the school doc ID we're operating under.
  const org = userDoc?.schoolId || userDoc?.orgId || null;
  return org === schoolId;
}, [currentUser, userDoc, schoolId, adminView]);

// ---------------- Invoice vs Stripe (must be defined BEFORE effects use it) ----------------
// ✅ Invoice vs Stripe (must be defined BEFORE effects use it)
const subForSchool = useMemo(() => {
  return schoolDoc?.subscription ?? userDoc?.subscription ?? null;
}, [schoolDoc, userDoc]);

// ✅ Single source of truth for invoice/offline detection
const isInvoiceSubscription = (sub) =>
  sub?.source === "invoice" || sub?.provider === "offline";

const isInvoiceSub = useMemo(() => {
  return isInvoiceSubscription(subForSchool);
}, [subForSchool]);

const subStatus = String(subForSchool?.status || "").toLowerCase();

const subRefreshRanRef = useRef(false);

useEffect(() => {
  if (!canAdmin || !schoolId || isInvoiceSub) return;

  // only once per session per school
  const key = `ysw_refreshedSub_${schoolId}`;
  if (subRefreshRanRef.current) return;
  if (sessionStorage.getItem(key) === "1") return;

  subRefreshRanRef.current = true;
  sessionStorage.setItem(key, "1");

  (async () => {
    try {
      const callable = httpsCallable(functions, "refreshSubscriptionDates");
      await callable({ force: true }); // safe + fixes stale dates without needing portal
      // no reload needed: onSnapshot should pick up updated start/end
    } catch (e) {
      console.warn("[auto-refresh] refreshSubscriptionDates failed:", e?.message || e);
    }
  })();
}, [canAdmin, schoolId, isInvoiceSub]);





// After returning from the billing portal, force a refresh once.
useEffect(() => {
   if (!canAdmin || isInvoiceSub) return
  const needs = localStorage.getItem("ysw_needsSubRefresh");
  if (!needs || !canAdmin) return;
  (async () => {
    try {
      const callable = httpsCallable(functions, "refreshSubscriptionDates");
      await callable({ force: true });
    } catch (e) {
      console.warn("[portal-return] refreshSubscriptionDates failed:", e?.message || e);
    } finally {
      localStorage.removeItem("ysw_needsSubRefresh");
      // Give Firestore a beat to sync, then reload UI
      setTimeout(() => window.location.reload(), 500);
    }
  })();
}, [canAdmin, isInvoiceSub]);

  /* ----------------------------- Live data (gated) ------------------------- */
  useEffect(() => {
    if (!canAdmin) return;
    const ref = doc(db, "schools", schoolId);
    const unsub = onSnapshot(
      ref,
      (snap) => setSchoolDoc(snap.exists() ? snap.data() : null),
      () => setPermErrors((e) => ({ ...e, school: true }))
    );
    return () => unsub();
  }, [canAdmin, schoolId]);

  useEffect(() => {
    if (!canAdmin) return;
    const qRef = query(collection(db, "teachers"), where("schoolId", "==", schoolId));
    const unsub = onSnapshot(
      qRef,
      (qs) => setTeachers(qs.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setPermErrors((e) => ({ ...e, teachers: true }))
    );
    return () => unsub();
  }, [canAdmin, schoolId]);

  useEffect(() => {
    if (!canAdmin) return;
    const qRef = query(collection(db, "students"), where("schoolId", "==", schoolId));
    const unsub = onSnapshot(
      qRef,
      (qs) => setStudents(qs.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setPermErrors((e) => ({ ...e, students: true }))
    );
    return () => unsub();
  }, [canAdmin, schoolId]);

  // 🔎 THEMES — collection-group across all classrooms, scoped to this school
  useEffect(() => {
    if (!canAdmin) return;

    // Order by createdAt is handy; Firestore will prompt for an index the first time.
    const qRef = query(
      collectionGroup(db, "themes"),
      where("schoolId", "==", schoolId),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      qRef,
      (qs) => {
        setThemesIndexHint("");
        setThemes(
          qs.docs.map((d) => ({
            id: d.id,
            ref: d.ref, // keep the DocumentReference for edit/delete
            ...d.data(),
          }))
        );
      },
      (e) => {
        setPermErrors((p) => ({ ...p, themes: true }));
        setThemesIndexHint(e?.message || "");
        console.warn("[themes] onSnapshot error:", e);
      }
    );
    return () => unsub();
  }, [canAdmin, schoolId]);

  /* ------------------------------- Derived UI ------------------------------ */
  const planName =
    userDoc?.subscription?.displayName ||
    schoolDoc?.subscription?.displayName ||
    schoolDoc?.package?.packageKey ||
    "School Plan";

  const billingInterval = normalizeBilling(
    userDoc?.subscription?.billingInterval ||
      schoolDoc?.subscription?.billingInterval ||
      schoolDoc?.package?.billing
  );

  const renewOn =
    asDateMaybe(schoolDoc?.subscription?.currentPeriodEnd) ||
    asDateMaybe(userDoc?.subscription?.currentPeriodEnd) ||
    null;


const OK_STATUSES = useMemo(() => new Set(["active", "trialing", "past_due"]), []);

const isExpired = useMemo(() => {
  const endMs = msFromAny(subForSchool?.currentPeriodEnd);
  const expiredByStatus = subStatus && !OK_STATUSES.has(subStatus);
  const expiredByDate = Number.isFinite(endMs) ? endMs < Date.now() : false;

  // If Stripe says active, assume not expired (even if end is stale).
  if (subStatus === "active") return false;

  return expiredByDate || expiredByStatus;
}, [subForSchool?.currentPeriodEnd, subStatus, OK_STATUSES]);

// -- Inferred period start if Firestore has null --

const rawStart =
  asDateMaybe(schoolDoc?.subscription?.currentPeriodStart) ||
  asDateMaybe(userDoc?.subscription?.currentPeriodStart) ||
  null;
const rawEnd =
  asDateMaybe(schoolDoc?.subscription?.currentPeriodEnd) ||
  asDateMaybe(userDoc?.subscription?.currentPeriodEnd) ||
  null;

// Use billingInterval we already normalized above
function backshiftStart(endDate, interval) {
  if (!endDate) return null;
  const d = new Date(endDate);
  if (interval === "annual") {
    d.setFullYear(d.getFullYear() - 1);
  } else {
    // default monthly
    d.setMonth(d.getMonth() - 1);
  }
  return d;
}

const inferredStart = rawStart || backshiftStart(rawEnd, billingInterval) || null;



  // refresh subscription dates if missing (callable only; avoid CORS to HTTP)
  useEffect(() => {
    if (isInvoiceSub) return;
    async function refreshIfMissing() {
      const subId =
  userDoc?.subscription?.id ||
  userDoc?.subscription?.stripe?.subscriptionId ||
  schoolDoc?.subscription?.id ||
  schoolDoc?.subscription?.stripe?.subscriptionId ||
  null;

      const missingStart = !rawStart;
const missingEnd = !rawEnd;
if (!subId || (!missingStart && !missingEnd)) return;

      try {
        const callable = httpsCallable(functions, "refreshSubscriptionDates");
        await callable({});
      } catch (e) {
        console.warn("[renewOn] refreshSubscriptionDates failed:", e?.message || e);
      }
    }
    refreshIfMissing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [userDoc?.subscription?.id, rawStart, rawEnd, isInvoiceSub]);


// If Stripe says 'active' but end is in the past, repair by refreshing dates.
useEffect(() => {
  if (!canAdmin || isInvoiceSub) return;
  const endMs = msFromAny(subForSchool?.currentPeriodEnd);
  const activeButPastEnd = subStatus === "active" && endMs && endMs < Date.now();
  if (!activeButPastEnd) return;

  (async () => {
    try {
      const callable = httpsCallable(functions, "refreshSubscriptionDates");
      await callable({ force: true });
      // Optional: reload once after refresh to pick up new values
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      console.warn("[repair] refreshSubscriptionDates failed:", e?.message || e);
    }
  })();
}, [canAdmin, subForSchool?.currentPeriodEnd, subStatus, isInvoiceSub]);



   // When coming back from Stripe Billing Portal (?portalReturn=1), force-refresh subscription
useEffect(() => {
  if (isInvoiceSub) return;
  const params = new URLSearchParams(loc.search || "");
  if (params.get("portalReturn") === "1") {
    (async () => {
      try {
        const callable = httpsCallable(functions, "refreshSubscriptionDates");
        // 'force' is optional; your function can ignore it if not used
        await callable({ force: true });
      } catch (e) {
        console.warn("[portalReturn] refreshSubscriptionDates failed:", e?.message || e);
      } finally {
        // Remove the query param without reloading the page
        window.history.replaceState({}, document.title, "/school-dashboard");
      }
    })();
  }
}, [loc.search, isInvoiceSub]);


// On focus/visibility, ping the backend to refresh subscription data
useEffect(() => {
  if (isInvoiceSub) return;
  const doRefresh = async () => {
    try {
      const callable = httpsCallable(functions, "refreshSubscriptionDates");
      await callable({});
    } catch (e) {
      // Non-fatal—just log
      console.warn("[focus refresh] refreshSubscriptionDates:", e?.message || e);
    }
  };

  const onFocus = () => doRefresh();
  const onVisible = () => {
    if (!document.hidden) doRefresh();
  };

  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisible);
  };
}, [isInvoiceSub]);



  const seatLimit =
    Number(schoolDoc?.package?.students ?? userDoc?.subscription?.seats?.students ?? 50);
  const usedSeats = students.length;
  const remainingSeats = Math.max(seatLimit - usedSeats, 0);

/* ---------- School billing window & pool (seats × 40) ---------- */
const now = new Date();

// Reuse the already-computed inferredStart (rawStart OR backshift from rawEnd)
const anchorStart = inferredStart;


let schoolPeriodStart = startOfCalendarMonth(now);
let schoolPeriodEndExclusive = endOfCalendarMonth(now);

if (anchorStart) {
  const mw = getMonthlyWindowAnchored(anchorStart, now);
  schoolPeriodStart = mw.start;
  schoolPeriodEndExclusive = mw.endExclusive;
}

// Display inclusive end date
const schoolPeriodEndDisplay = schoolPeriodEndExclusive
  ? new Date(schoolPeriodEndExclusive.getTime() - 24 * 60 * 60 * 1000)
  : null;



  // Sum all student stories in the school's billing window
  const poolUsed = useMemo(() => {
    return students.reduce((acc, s) => {
      const used = sumStoriesInRange(s?.readingActivity || [], schoolPeriodStart, schoolPeriodEndExclusive);
      return acc + used;
    }, 0);
  }, [students, schoolPeriodStart, schoolPeriodEndExclusive]);

  // Included is based on SEATS × 40 (not active students)
  const poolIncluded = seatLimit * PER_STUDENT_CAP;
  const poolRemaining = Math.max(poolIncluded - poolUsed, 0);

  // Per-class usage in the school billing window
  const classes = useMemo(() => {
    const m = new Map(); // className -> { count, used }
    // count students per class
    students.forEach((s) => {
      const key = s.className || "—";
      if (!m.has(key)) m.set(key, { count: 0, used: 0 });
      m.get(key).count += 1;
    });
    // add usage per class (stories in window)
    students.forEach((s) => {
      const key = s.className || "—";
      const used = sumStoriesInRange(s?.readingActivity || [], schoolPeriodStart, schoolPeriodEndExclusive);
      if (!m.has(key)) m.set(key, { count: 0, used: 0 });
      m.get(key).used += used;
    });

    return Array.from(m.entries()).map(([className, v]) => {
      const limit = v.count * PER_STUDENT_CAP;
      return {
        className,
        count: v.count,
        used: v.used,
        limit,
        remaining: Math.max(limit - v.used, 0),
      };
    });
  }, [students, schoolPeriodStart, schoolPeriodEndExclusive]);

  // Keep your existing avg time / historical block
  const classProgress = useMemo(() => {
    const sums = {};
    students.forEach((s) => {
      const cn = s.className || "—";
      if (!sums[cn]) sums[cn] = { time: 0, stories: 0, count: 0 };
      sums[cn].time += Number(s.totalReadingTime || 0);
      sums[cn].stories += Number(s.storiesRead || 0);
      sums[cn].count += 1;
    });
    return Object.entries(sums).map(([className, v]) => ({
      className,
      avgReadingTime: v.count ? Math.round(v.time / 60 / v.count) : 0,
      storiesGenerated: v.stories,
      count: v.count,
    }));
  }, [students]);

  const filteredStudents = students.filter((s) => {
    const matchesClass = filterClass ? s.className === filterClass : true;
    const name = `${s.firstName || ""} ${s.lastName || ""}`.trim().toLowerCase();
    const matchesSearch = searchTerm ? name.includes(searchTerm.toLowerCase()) : true;
    return matchesClass && matchesSearch;
  });

  /* ------------------------------- Handlers -------------------------------- */
  const handleLogout = async () => {
    try { await signOut(sharedAuth); } catch {}
    localStorage.removeItem("schoolId");
    setNoSchool(false);
    navigate("/");
  };

  const handleOpenModal = async (student) => {
    // Optional: ensure user doc is stamped (helpful during debugging)
    try {
      const uid = sharedAuth.currentUser?.uid;
      if (uid) {
        const uSnap = await getDoc(doc(db, "users", uid));
        if (!uSnap.exists() || (uSnap.data()?.schoolId ?? "") !== schoolId) {
          console.warn("[debug] users doc missing/mismatched schoolId relative to dashboard schoolId.");
        }
      }
    } catch {}
    setSelectedStudent(student);
    setOpenStudentModal(true);
  };
  const handleCloseModal = () => {
    setOpenStudentModal(false);
    setSelectedStudent(null);
  };


// Start a fresh subscription Checkout session to renew an expired school plan
async function handleRenewSubscription() {
  try {
    // ✅ ADD THIS BLOCK HERE (top of the try)
    if (isInvoiceSub) {
      alert("This school is billed via invoice. Please contact support to renew.");
      return;
    }
    const u = sharedAuth.currentUser;
    if (!u) {
      alert("Please sign in again to renew your school subscription.");
      return;
    }

    // Prefer school subscription details, then fall back to userDoc
    const priceId =
      schoolDoc?.subscription?.stripe?.priceId ||
      schoolDoc?.subscription?.priceId ||
      userDoc?.subscription?.stripe?.priceId ||
      userDoc?.subscription?.priceId ||
      null;

    if (!priceId) {
      alert(
        "We couldn’t find your existing school plan details.\n\n" +
        "Please contact support or choose your school package again."
      );
      navigate("/schools");
      return;
    }

    setRenewing(true);

    await u.getIdToken(true);
    const idToken = await u.getIdToken();

    const resp = await fetch(
      "https://us-central1-yourstoryworld-df05d.cloudfunctions.net/createSubscriptionCheckoutSessionHttp",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          priceId,
          quantity: 1,
          // Use the same BillingSuccessRedirect pattern as families/personal
          successUrl: `${window.location.origin}/billing-success?to=/school-dashboard`,
          cancelUrl: `${window.location.origin}/schools`,
          metadata: {
            planKind: "school",
            schoolId,
          },
        }),
      }
    );

    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(json?.error?.message || `Renewal failed (${resp.status}).`);
    }
    if (!json?.url) {
      throw new Error("No checkout URL returned from Stripe.");
    }

    // Help any redirects know where to land
    localStorage.setItem("lastDashboard", "/school-dashboard");

    window.location.href = json.url;
  } catch (e) {
    console.error("School renew subscription error:", e);
    alert(e?.message || "Could not start renewal. Please try again.");
  } finally {
    setRenewing(false);
  }
}


  // portal opener
  async function openBillingPortal() {
    setOpeningPortal(true);
    try {
      const callable = httpsCallable(functions, "createPortalSession");
      const { data } = await callable({
  returnUrl: window.location.origin + "/school-dashboard?portalReturn=1",
});

      if (!data?.url) throw new Error("No portal URL returned");
      localStorage.setItem("ysw_needsSubRefresh", "1");
      window.location.assign(data.url);
      return;
    } catch (e1) {
      console.warn("[billing] callable failed, trying HTTP fallback", e1?.message || e1);
      try {
        const u = sharedAuth.currentUser;
        if (!u) throw new Error("Not signed in");
        const token = await u.getIdToken(true);
        const resp = await fetch(
          "https://us-central1-yourstoryworld-df05d.cloudfunctions.net/createPortalSessionHttp",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
  returnUrl: window.location.origin + "/school-dashboard?portalReturn=1",
}),

          }
        );
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(json?.error || json?.error?.message || `HTTP ${resp.status}`);
        if (!json?.url) throw new Error("No portal URL returned");
        localStorage.setItem("ysw_needsSubRefresh", "1");
        window.location.assign(json.url);
        return;
      } catch (e2) {
        console.error("Billing portal error:", e2);
        alert(e2?.message || "Couldn't open the billing portal. Please try again.");
      }
    } finally {
      setOpeningPortal(false);
    }
  }

  const buttonStyle = {
    marginBottom: "15px",
    padding: "10px",
    width: "100%",
    backgroundColor: "#007BFF",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
  };

  /* ------------------------------- Gated views ----------------------------- */
  if (!currentUser) {
    return (
      <div className="page-container" style={{ padding: 40 }}>
        <h2>Please sign in</h2>
      </div>
    );
  }

  if (resolvingSchool || userLoading) {
    return (
      <div className="page-container" style={{ padding: 40 }}>
        <h2>Loading your school…</h2>
      </div>
    );
  }

  if (!schoolId || noSchool) {
    return (
      <div className="page-container" style={{ padding: 40 }}>
        <h2>No school selected yet</h2>
        <p>We couldn’t detect a school on your account. Choose an existing one or create a new school.</p>
        <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
          <button className="header-button" onClick={() => navigate("/schools")}>
            Select / Create School
          </button>
          <button
            className="header-button"
            onClick={() => {
              localStorage.removeItem("schoolId");
              navigate("/schools");
            }}
            style={{ backgroundColor: "#e11d48", color: "#fff" }}
          >
            Reset & Choose
          </button>
        </div>
      </div>
    );
  }

  if (!canAdmin) {
    return (
      <div className="page-container" style={{ padding: 40 }}>
        <h2>School access pending</h2>
        <p>
          We couldn’t confirm your permission for this school yet. If you just subscribed or logged in,
          give it a moment and refresh.
        </p>
        <button className="header-button" onClick={() => window.location.reload()}>
          Refresh
        </button>
      </div>
    );
  }

  /* ---------- NEW: hard-lock when expired ---------- */
const subEnd = asDateMaybe(subForSchool?.currentPeriodEnd) || null;

const blockIfExpired = () => {
  if (!isExpired) return false;
  alert("Your school subscription has expired. Please renew in Billing to continue.");
  return true;
};

if (isExpired) {
  return (
    <div
      className="page-container"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: 50, cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav style={{ display: "flex", gap: 15, justifyContent: "flex-end" }}>
          <button onClick={() => navigate("/help")} className="header-button">Help</button>
          <button
            onClick={handleLogout}
            className="header-button"
            style={{ backgroundColor: "blue", color: "#fff", fontWeight: "bold" }}
          >
            Logout
          </button>
        </nav>
      </header>

      <div style={{ flex: 1, display: "grid", placeItems: "center", padding: 24 }}>
        <div
          style={{
            width: "100%",
            maxWidth: 600,
            background: "rgba(255,255,255,0.96)",
            borderRadius: 12,
            boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
            padding: 24,
            textAlign: "center",
          }}
        >
          <h1 style={{ marginTop: 0, color: "#0f172a" }}>Subscription required</h1>
          <p style={{ color: "#334155" }}>
            Your school subscription ended on <b>{formatDate(subEnd)}</b>. To continue using YourStoryWorld,
            please renew your subscription.
          </p>

          <div style={{ marginTop: 16, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
           {isInvoiceSub ? (
  <button
    onClick={() => navigate("/contact")}
    className="header-button"
    style={{ backgroundColor: "#0ea5e9", color: "#fff" }}
    title="Invoice / bank transfer support"
  >
    Contact Support
  </button>
) : (
  <button
    onClick={openBillingPortal}
    className="header-button"
    style={{ backgroundColor: "#10b981", color: "#fff" }}
    disabled={openingPortal}
    title="Open Stripe billing portal"
  >
    {openingPortal ? "Opening billing…" : "Manage Billing"}
  </button>
)}

            <button
              onClick={() => window.location.reload()}
              className="header-button"
              style={{ backgroundColor: "#0ea5e9", color: "#fff" }}
            >
              Refresh
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
            Status: <b>{subStatus || "unknown"}</b>
          </div>
        </div>
      </div>

      <footer className="page-footer">
        <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
      </footer>
    </div>
  );
}


  /* ---------------------------------- View --------------------------------- */
  return (
    <div
      className="page-container"
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav style={{ display: "flex", gap: "15px", justifyContent: "flex-end" }}>
          <button onClick={() => navigate("/help")} className="header-button">
            Help
          </button>
          <button onClick={() => navigate("/")} className="header-button">
            Home
          </button>
          <button
            onClick={handleLogout}
            className="header-button"
            style={{ backgroundColor: "blue", color: "white", fontWeight: "bold" }}
          >
            Logout
          </button>
        </nav>
      </header>

      

      {(permErrors.teachers || permErrors.students || permErrors.themes) && (
        <div
          style={{
            margin: "12px 20px 0",
            background: "#fff4e5",
            border: "1px solid #ffd8a8",
            color: "#663c00",
            borderRadius: 8,
            padding: "10px 14px",
          }}
        >
          {permErrors.teachers && <div>Missing or insufficient permissions for <b>teachers</b>.</div>}
          {permErrors.students && <div>Missing or insufficient permissions for <b>students</b>.</div>}
          {permErrors.themes && (
            <div>
              Missing or insufficient permissions for <b>themes</b>.
              {themesIndexHint.toLowerCase().includes("create index") && (
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  Firestore may require a composite index for the themes query. Check the console for a
                  “create index” link and click it once.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          padding: "20px",
          gap: "20px",
          alignItems: "flex-start",
          flexWrap: "wrap",
          overflowX: "auto",
        }}
      >
        {/* Sidebar */}
        <div style={{ width: isMobile ? "100%" : "260px", display: "flex", flexDirection: "column", gap: "20px" }}>
          <aside
            style={{
              background: "rgba(230, 245, 255, 0.95)",
              padding: "20px",
              borderRadius: "10px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            <h2 style={{ color: "#007BFF", marginBottom: "10px" }}>
              {userDoc?.schoolName || schoolDoc?.name || "School"}
            </h2>

            {/* Seats */}
            <div
              style={{
                marginTop: 10,
                background: "#f6f7fb",
                padding: 12,
                borderRadius: 8,
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>🎟️ Seats</div>
              <div style={{ fontSize: 14 }}>
                Used / Limit: <strong>{usedSeats}</strong> / <strong>{seatLimit}</strong>
              </div>
              <div style={{ fontSize: 14 }}>
                Remaining: <strong>{remainingSeats}</strong>
              </div>
              <div style={{ height: 8, background: "#e0e7ff", borderRadius: 999, overflow: "hidden", marginTop: 8 }}>
                <div
                  style={{
                    width: `${Math.min(100, Math.round((usedSeats / Math.max(1, seatLimit)) * 100))}%`,
                    height: "100%",
                    background: "#3b82f6",
                  }}
                />
              </div>
            </div>

            {/* 📚 Story Pool */}
            <div
              style={{
                marginTop: 10,
                background: "#f6f7fb",
                padding: 12,
                borderRadius: 8,
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                📚 Story Pool (This Month)
              </div>
              <div style={{ fontSize: 12, marginBottom: 6, color: "#334155" }}>
                Period: <strong>{formatDate(schoolPeriodStart)} → {formatDate(schoolPeriodEndDisplay)}</strong>
                <br />
                Resets on <strong>{formatDate(schoolPeriodEndExclusive)}</strong>
              </div>
              <div style={{ fontSize: 14 }}>
                Used / Included: <strong>{poolUsed}</strong> / <strong>{poolIncluded}</strong>
              </div>
              <div style={{ fontSize: 14 }}>
                Remaining: <strong>{poolRemaining}</strong>
              </div>

              <div style={{ height: 8, background: "#fee2e2", borderRadius: 999, overflow: "hidden", marginTop: 8 }}>
                <div
                  style={{
                    width: `${poolIncluded ? Math.min(100, Math.round((poolUsed / poolIncluded) * 100)) : 0}%`,
                    height: "100%",
                    background: "#ef4444",
                  }}
                />
              </div>
              <div style={{ fontSize: 12, marginTop: 8, color: "#334155" }}>
                Per-student limit: <strong>{PER_STUDENT_CAP}</strong> • Included = <strong>seats × {PER_STUDENT_CAP}</strong>
              </div>
              <div style={{ fontSize: 12, marginTop: 4, color: "#475569" }}>
  This is your school’s monthly story allowance window (it resets every month).
</div>
              </div>

            {/* Counters */}
            <div
              style={{
                marginTop: 10,
                background: "#f6f7fb",
                padding: 12,
                borderRadius: 8,
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>👩‍🏫 Teachers</div>
              <div style={{ fontSize: 14 }}>{teachers.length}</div>
            </div>
            <div
              style={{
                marginTop: 10,
                background: "#f6f7fb",
                padding: 12,
                borderRadius: 8,
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>👧 Students</div>
              <div style={{ fontSize: 14 }}>{students.length}</div>
            </div>

            <div style={{ marginTop: "10px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input type="checkbox" checked={diversityMode} onChange={() => setDiversityMode(!diversityMode)} />
                <span>Enable Diversity Mode</span>
              </label>
              <p style={{ fontSize: "12px", color: "#555", marginTop: "5px" }}>
                Turning this on ensures characters, settings, and themes reflect diverse cultures and backgrounds.
              </p>
            </div>
          </aside>

          {/* Admin Tools */}
          <div
            style={{
              background: "rgba(230, 245, 255, 0.95)",
              padding: "20px",
              borderRadius: "10px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            <h3 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF" }}>Admin Tools</h3>
            <p style={{ marginBottom: "20px", fontSize: "0.9rem", color: "#D2691E" }}>
              Manage your school accounts, track activity, and review metrics.
            </p>

            <p style={{ marginBottom: "10px", fontSize: "0.9rem", color: "#555" }}>
              Manage teacher accounts and roles across your school.
            </p>
            <button
  onClick={() => { if (!blockIfExpired()) navigate("/manage-teachers"); }}
  style={{ ...buttonStyle, opacity: isExpired ? 0.6 : 1, cursor: isExpired ? "not-allowed" : "pointer" }}
  disabled={isExpired}
>
  Manage Teachers
</button>

            <p style={{ marginBottom: "10px", fontSize: "0.9rem", color: "#555" }}>
              Add, remove, or update student profiles and manage access to story generation tools.
            </p>
            <button
  onClick={() => { if (!blockIfExpired()) navigate("/manage-students"); }}
  style={{ ...buttonStyle, opacity: isExpired ? 0.6 : 1, cursor: isExpired ? "not-allowed" : "pointer" }}
  disabled={isExpired}
>
  Manage Students
</button>
          </div>
        </div>

                        {/* Main */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1
            style={{
              fontFamily: "Montserrat, sans-serif",
              color: "#007BFF",
              textAlign: "center",
            }}
          >
            Welcome to Your School Dashboard
          </h1>

          {/* 🔹 Quick-start teaser + expandable guide */}
          <div
            style={{
              maxWidth: 900,
              margin: "0 auto 12px",
            }}
          >
            {/* Teaser bar */}
            <div
              style={{
                background: "rgba(255,255,255,0.9)",
                padding: "10px 14px",
                borderRadius: "999px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                fontSize: "0.9rem",
              }}
            >
              <span style={{ color: "#4b5563" }}>
                🧭 New to the School Dashboard? View the step-by-step setup guide.
              </span>
              <Button
                size="small"
                variant="contained"
                onClick={() => setGuideExpanded((v) => !v)}
                style={{
                  textTransform: "none",
                  backgroundColor: guideExpanded ? "#6b7280" : "#007BFF",
                  fontSize: "0.8rem",
                  padding: "4px 12px",
                  borderRadius: 999,
                }}
              >
                {guideExpanded ? "Hide guide" : "Show quick-start"}
              </Button>
            </div>

            {/* Expanded panel */}
            {guideExpanded && (
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.97)",
                  padding: "18px 20px 16px",
                  borderRadius: "12px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.14)",
                  marginTop: "10px",
                  fontSize: "0.95rem",
                  color: "#1f2933",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    marginBottom: 6,
                    fontSize: "1.05rem",
                    color: "#007BFF",
                  }}
                >
                  Quick start for schools
                </h2>
                <p style={{ margin: "4px 0 10px", color: "#4b5563" }}>
                  Follow these steps to get YourStoryWorld set up across your school.
                </p>

                <ol
                  style={{
                    paddingLeft: "1.1rem",
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  <li>
                    <strong>Set up teachers</strong> — use{" "}
                    <em>Manage Teachers</em> in the left panel to create teacher
                    accounts linked to this school. Teachers get access to the{" "}
                    <em>Teacher Dashboard</em>, <em>Classroom Insights</em> and
                    student data for their classes.
                  </li>
                  <li style={{ marginTop: 6 }}>
                    <strong>Add classes &amp; students</strong> — go to{" "}
                    <em>Manage Students</em> to create classes, add students
                    (individually, in bulk text or via CSV) and generate
                    usernames/passwords. Each student automatically gets their
                    own login and dashboard.
                  </li>
                  <li style={{ marginTop: 6 }}>
                    <strong>Share student logins</strong> — export or copy
                    usernames and passwords from <em>Manage Students</em> and
                    share them securely with staff / families as needed.
                  </li>
                  <li style={{ marginTop: 6 }}>
                    <strong>Use in lessons</strong> — teachers sign in, open the{" "}
                    <em>Teacher Dashboard</em>, and guide students to create and
                    read stories. Activity feeds into <em>Classroom Insights</em>,{" "}
                    <em>School Metrics</em> and this School Dashboard.
                  </li>
                  <li style={{ marginTop: 6 }}>
                    <strong>Assign themes &amp; monitor progress</strong> — use
                    the <em>Custom Themes</em> tab below to set whole-class
                    writing or reading tasks, and use{" "}
                    <em>Classroom Insights</em> / <em>School Metrics</em> to
                    review engagement, reading time and genres explored.
                  </li>
                  <li style={{ marginTop: 6 }}>
  <strong>Content stays child-safe</strong> — all generated stories are filtered to remove 
  inappropriate content. This includes violence, explicit or romantic themes, harmful stereotypes, 
  adult language, drugs/alcohol, and frightening material. Teachers can use stories confidently 
  during lessons knowing the system is safeguarded.
</li>
                </ol>

                                <hr
                  style={{
                    border: "none",
                    borderTop: "1px solid #e5e7eb",
                    margin: "12px 0 10px",
                  }}
                />

                <h2
  style={{
    margin: 0,
    marginBottom: 6,
    fontSize: "1.05rem",
    color: "#007BFF",
    fontFamily: "inherit",   // ← this ensures the same font as the H2 above
  }}
>
  What do “Seats” and “Story Pool” mean?
</h2>


                <ul
                  style={{
                    paddingLeft: "1.1rem",
                    margin: 0,
                    lineHeight: 1.6,
                    fontSize: "0.9rem",
                    color: "#374151",
                  }}
                >
                  <li style={{ marginBottom: 6 }}>
                    <strong>Seats</strong> (left sidebar) = the number of{" "}
                    <strong>student logins</strong> included in your school plan.
                    <br />
                    <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                      • <em>Used</em> = how many student accounts you have created.
                      <br />
                      • <em>Remaining</em> = how many more students you can add
                      before you reach your limit.
                    </span>
                  </li>

                  <li style={{ marginBottom: 6 }}>
                    <strong>Story Pool (This Period)</strong> = the total number
                    of <strong>stories your whole school can generate</strong> in
                    the current billing period.
                    <br />
                    <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                      • The included pool is calculated as{" "}
                      <em>seats × {PER_STUDENT_CAP}</em> stories.
                      <br />
                      • <em>Used</em> = all stories generated by all students in this
                      period.
                      <br />
                      • <em>Remaining</em> = how many stories you still have
                      available before hitting the cap.
                    </span>
                  </li>

                  <li style={{ marginBottom: 0 }}>
                    <strong>Class tiles</strong> in the <em>School Summary</em>{" "}
                    section use the same idea, but per class (number of students
                    in that class × {PER_STUDENT_CAP}). This helps you see which
                    classes are reading heavily and which may need encouragement.
                  </li>
                </ul>

                <p
                  style={{
                    marginTop: 8,
                    fontSize: "0.8rem",
                    color: "#6b7280",
                  }}
                >
                  Tip: if your school regularly reaches the story pool limit, you
                  can upgrade your plan or contact us about add-ons or a larger
                  package.
                </p>


                <p
                  style={{
                    marginTop: 10,
                    fontSize: "0.85rem",
                    color: "#6b7280",
                  }}
                >
                  Need a full guide or data summary for your trust or SLT? Visit{" "}
                  <a
                    href="/help"
                    style={{ color: "#007BFF", textDecoration: "underline" }}
                  >
                    Help &amp; FAQs
                  </a>{" "}
                  or{" "}
                  <a
                    href="/data-protection"
                    style={{ color: "#007BFF", textDecoration: "underline" }}
                  >
                    Data Protection &amp; Security for Schools
                  </a>
                  .
                </p> 
                <div
  style={{
    background: "#f0f9ff",
    padding: "12px 16px",
    borderRadius: "10px",
    marginTop: "14px",
    border: "1px solid #bae6fd",
  }}
>
  <h4 style={{ margin: 0, color: "#0284c7", fontSize: "0.95rem" }}>
    🌟 How YourStoryWorld Keeps Content Safe
  </h4>
  <p style={{ marginTop: "6px", fontSize: "0.85rem", color: "#0f172a" }}>
    Stories generated for students are automatically screened for age suitability. 
    Themes involving violence, explicit content, unsafe behaviour, discriminatory 
    language, or mature topics are blocked. YourStoryWorld follows UK safeguarding 
    expectations and the Age-Appropriate Design Code.
  </p>
</div>

              </div>
            )}
          </div>

          {/* Billing Card */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              padding: "24px",
              borderRadius: "10px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              marginBottom: "24px",
            }}
          >


            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                gap: 12,
              }}
            >
              <div>
                <h2 style={{ margin: 0, color: "#007BFF" }}>Billing</h2>
                <p style={{ marginTop: 4, color: "#555" }}>
  {isInvoiceSub
    ? "This school is billed via invoice. Renewals and changes are handled manually."
    : "Update payment details, change plans, or download invoices."}
</p>

              </div>
              <div style={{ textAlign: isMobile ? "left" : "right" }}>
  <div style={{ fontWeight: 700 }}>
    Plan: {planName} {billingInterval !== "—" ? `(${billingInterval})` : ""}
  </div>
  <div>Renews: {formatDate(renewOn)}</div>

  {/* 🔁 Auto-renew note */}
 {!isInvoiceSub && (
  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
    Your school plan renews automatically each{" "}
    {billingInterval === "annual" ? "year" : "month"} unless you change or cancel it
    in billing.
  </div>
)}

</div>

            </div>

            <div
  style={{
    marginTop: 12,
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
  }}
>
  {isInvoiceSub ? (
    <>
      <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.5 }}>
        This school is billed via <b>invoice / purchase order</b>.
        <br />
        Renewals, seat changes and cancellations are handled manually.
      </div>

      <button
        onClick={() => navigate("/contact")}
        className="header-button"
        style={{ backgroundColor: "#0ea5e9", color: "#fff" }}
      >
        Contact Support
      </button>
    </>
  ) : (
    <>
      <button
        onClick={handleRenewSubscription}
        disabled={!isExpired || renewing}
        style={{
          padding: "10px 16px",
          backgroundColor: isExpired ? "#f97316" : "#9ca3af",
          color: "white",
          border: "none",
          borderRadius: "6px",
          cursor: isExpired ? "pointer" : "not-allowed",
        }}
      >
        {renewing ? "Starting renewal…" : "Renew Subscription"}
      </button>

      <button
        onClick={openBillingPortal}
        className="header-button"
        style={{ backgroundColor: "#10b981", color: "#fff" }}
        disabled={openingPortal}
      >
        {openingPortal ? "Opening billing…" : "Manage Billing"}
      </button>
    </>
  )}
</div>



          </div>

          {/* 🏫 School Summary */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.95)",
              padding: "30px",
              borderRadius: "10px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              marginBottom: "30px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                gap: 12,
              }}
            >
              <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", margin: 0 }}>
                🏫 School Summary
              </h2>
              <Tooltip title={students.length ? "" : "Add at least one student to view School Metrics"} arrow>
                <span>
                  <Button
  onClick={() => { if (!blockIfExpired()) navigate("/school-metrics"); }}
  disabled={!students.length || isExpired}
  variant="contained"
>
  School Metrics
</Button>
                </span>
              </Tooltip>
            </div>

            <p style={{ marginTop: 12, color: "#444" }}>
              Each class tile shows <b>stories used</b> in this period vs a nominal class limit of{" "}
              <b>#students × {PER_STUDENT_CAP}</b>. The overall cap is school-wide.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                gap: "20px",
                marginTop: "15px",
              }}
            >
              {classes.map((cls) => {
                const fillPercent = cls.limit ? Math.min((cls.used / cls.limit) * 100, 100) : 0;
                const over = cls.used > cls.limit;
                return (
                  <div
                    key={cls.className}
                    style={{
                      background: "#ffffff",
                      padding: "15px",
                      borderRadius: "10px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <strong>{cls.className}</strong>
                    <p style={{ margin: "6px 0" }}>
                      Students: <b>{cls.count}</b>
                    </p>
                    <p style={{ margin: "6px 0" }}>
                      Used / Limit: <b>{cls.used}</b> / <b>{cls.limit}</b>
                    </p>
                    <div
                      style={{
                        height: "10px",
                        borderRadius: "5px",
                        backgroundColor: "#eee",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${fillPercent}%`,
                          height: "100%",
                          backgroundColor: over ? "#f44336" : fillPercent > 85 ? "#ff9800" : "#4caf50",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                    <p style={{ marginTop: 6, fontSize: 12, color: "#475569" }}>
                      Remaining: <b>{cls.remaining}</b>
                    </p>
                  </div>
                );
              })}
              {!classes.length && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    background: "#fff",
                    padding: 16,
                    borderRadius: 10,
                    border: "1px dashed #ddd",
                    color: "#666",
                  }}
                >
                  No classes yet. Add students to see class summaries.
                </div>
              )}
            </div>
          </div>

          {/* Class progress + tabs */}
  <div
    style={{
      background: "rgba(255, 255, 255, 0.9)",
      padding: "24px",
      borderRadius: "10px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      marginBottom: "40px",   // ⬅️ extra space before footer
    }}
  >
            <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF" }}>📘 Class Progress</h2>
            {classProgress.map((cls) => (
              <p key={cls.className}>
                <strong>{cls.className}:</strong> Avg Reading Time: {cls.avgReadingTime} mins, Stories Generated{" "}
                {cls.storiesGenerated}
              </p>
            ))}

           <div style={{ marginTop: "20px" }}>
  {/* ✅ Wrap Tabs in a horizontal scroll container */}
  <div style={{ overflowX: "auto" }}>
    <Tabs
      value={activeTab}
      onChange={(e, v) => setActiveTab(v)}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"          // ✅ allow scrolling when narrow
      scrollButtons="auto"          // ✅ show scroll arrows when needed
      sx={{
        "& .MuiTab-root": {
          fontWeight: "bold",
          whiteSpace: "nowrap",     // ✅ keep labels in one line
          minWidth: "auto",
        },
        "& .Mui-selected": { color: "#007BFF !important" },
      }}
    >
      <Tab label="📚 Classroom Insights" />
      <Tab label="🎨 Custom Themes" />
      <Tab label="🎓 View Student Data" />
    </Tabs>
  </div>


              {activeTab === 0 && (
                <div style={{ paddingTop: "20px", textAlign: "center" }}>
                  <h3>Access detailed analytics about classroom participation and reading trends</h3>
                  <Tooltip title={students.length ? "" : "Add at least one student to view Classroom Insights"} arrow>
                    <span>
                      <Button
  onClick={() => { if (!blockIfExpired()) navigate("/classroom-insights"); }}
  disabled={!students.length || isExpired}
  variant="contained"
  style={{ marginRight: "10px" }}
>
  Classroom Insights
</Button>
                    </span>
                  </Tooltip>
                </div>
              )}

              {activeTab === 1 && (
                <div style={{ paddingTop: "20px" }}>
                  <h3>Custom Themes</h3>
                  <button
  onClick={() => {
    if (blockIfExpired()) return;
    setIsThemeModalOpen(true);
    setEditThemeData(null);
    setSelectedTheme("");
    setThemeSetting("");
    setThemeDescription("");
    setThemeComment("");
    setExpiryDate("");
    setSelectedClasses([]);
  }}
  style={{
    marginBottom: "20px",
    padding: "10px 20px",
    backgroundColor: isExpired ? "#9ca3af" : "#28a745",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: isExpired ? "not-allowed" : "pointer",
  }}
  disabled={isExpired}
>
  ➕ Assign New Theme to Class
</button>


                  <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF" }}>🎓 Active Assigned Themes</h2>
                  {themes.length === 0 ? (
                    <p style={{ color: "#777" }}>No active themes have been assigned.</p>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: "20px",
                        marginTop: "20px",
                      }}
                    >
                      {themes.map((theme) => {
                        const exp = asDateMaybe(theme.expiryDate);
                        return (
                          <div
                            key={`${theme.id}-${theme.className || "unknown"}`}
                            style={{
                              backgroundColor: "#ffffff",
                              padding: "20px",
                              borderRadius: "10px",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.08)",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            <div>
                              <h3
                                style={{
                                  marginBottom: "8px",
                                  color: "#007BFF",
                                  fontFamily: "Arial, Helvetica, sans-serif",
                                  fontWeight: "600",
                                }}
                              >
                                {theme.theme}
                              </h3>
                              <p><strong>Class:</strong> {theme.className || "—"}</p>
                              <p><strong>Setting:</strong> {theme.setting || "-"}</p>
                              <p><strong>Description:</strong> {theme.description || "-"}</p>
                              <p><strong>Message:</strong> {theme.comment || "-"}</p>
                              <p>
                                <strong>Classes:</strong>{" "}
                                {Array.isArray(theme.classes) && theme.classes.length ? theme.classes.join(", ") : theme.className || "-"}
                              </p>
                              <p><strong>Expires:</strong> {exp ? formatDate(exp) : "-"}</p>
                            </div>
                            <div style={{ marginTop: "10px", textAlign: "right" }}>
                              <button
                                onClick={async () => {
                                 if (blockIfExpired()) return;
                                  try {
                                    await deleteDoc(theme.ref);
                                    toast.success("🗑️ Theme deleted.");
                                  } catch (e) {
                                    console.error("Delete theme error:", e);
                                    toast.error(
                                      e?.code === "permission-denied"
                                        ? "You don’t have permission to delete themes for this school."
                                        : `Couldn't delete the theme: ${e?.message || "Unknown error"}`
                                    );
                                  }
                                }}
                                style={{
    background: "none",
    color: isExpired ? "#fca5a5" : "#FF4D4D",
    border: "none",
    cursor: isExpired ? "not-allowed" : "pointer",
    fontWeight: "bold",
    opacity: isExpired ? 0.6 : 1,
  }}
  disabled={isExpired}
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

             {activeTab === 2 && (
  <div style={{ paddingTop: "20px" }}>
    <h3>View Student Data</h3>

    {/* Filters row */}
    <div style={{ display: "flex", gap: "15px", marginBottom: "15px" }}>
      <select
        value={filterClass}
        onChange={(e) => setFilterClass(e.target.value)}
        style={{ padding: "10px", borderRadius: "5px" }}
      >
        <option value="">All Classes</option>
        {Array.from(new Set(students.map((s) => s.className).filter(Boolean))).map((cls) => (
          <option key={cls} value={cls}>
            {cls}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Search by student name"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ padding: "10px", flex: 1, borderRadius: "5px" }}
      />
    </div>

    {/* Empty states */}
    {!filterClass && !searchTerm ? (
      <p style={{ color: "#555", fontStyle: "italic" }}>
        Please select a class or search for a student to view results.
      </p>
    ) : filteredStudents.length === 0 ? (
      <p style={{ color: "#777" }}>No students match your criteria.</p>
    ) : (
      // ✅ New scroll wrapper so the table doesn't feel cramped on smaller screens
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth: "800px", // <-- key line: gives the table room, adds horizontal scroll if needed
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f0f8ff" }}>
              <th>Name</th>
              <th>Class</th>
              <th>Stories</th>
              <th>Time (min)</th>
              <th>Avg. Time</th>
              <th>Genres</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((s) => {
              const genres =
                Array.isArray(s.genresExplored) || typeof s.genresExplored === "object"
                  ? s.genresExplored
                  : s.favoriteGenres || {};
              const genresText = Array.isArray(genres)
                ? Array.from(new Set(genres)).join(", ")
                : Object.keys(genres || {}).join(", ");
              const totalMin = Math.round(Number(s.totalReadingTime || 0) / 60);
              const stories = Number(s.storiesRead || 0);
              const avgMin = stories ? Math.round(totalMin / stories) : 0;

              return (
                <tr key={s.id} style={{ borderBottom: "1px solid #ccc" }}>
                  <td>{`${s.firstName || ""} ${s.lastName || ""}`.trim() || "—"}</td>
                  <td>{s.className || "—"}</td>
                  <td>{stories}</td>
                  <td>{totalMin}</td>
                  <td>{avgMin}</td>
                  <td>{genresText || "—"}</td>
                  <td style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => handleOpenModal(s)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#007BFF",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                      }}
                    >
                      View Insights
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

            </div>
          </div>
        </div>

        {/* Student Insights Modal */}
        <Modal open={openStudentModal} onClose={handleCloseModal} aria-labelledby="student-insights-title">
          <Box
            sx={{
              backgroundColor: "white",
              margin: "50px auto",
              padding: "30px",
              borderRadius: "10px",
              width: "80%",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: 24,
            }}
          >
            {selectedStudent && (
              <>
                <h2 id="student-insights-title" style={{ color: "#007BFF" }}>
                  {selectedStudent.firstName} {selectedStudent.lastName}
                </h2>
                <p><strong>Class:</strong> {selectedStudent.className || "—"}</p>
                <p><strong>Stories Read:</strong> {Number(selectedStudent.storiesRead || 0)}</p>
                <p><strong>Total Reading Time:</strong> {Math.round(Number(selectedStudent.totalReadingTime || 0) / 60)} mins</p>

                {/* Genres */}
                {(() => {
                  const g = normalizeGenres(
                    selectedStudent.genresExplored || selectedStudent.favoriteGenres || {}
                  );
                  if (!g.labels.length) return null;
                  return (
                    <div
                      style={{
                        marginTop: "30px",
                        backgroundColor: "#f9f9f9",
                        padding: "20px",
                        borderRadius: "12px",
                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                      }}
                    >
                      <h3 style={{ fontSize: "1rem", marginBottom: "10px", color: "#333" }}>Favorite Genres</h3>
                      <div style={{ height: "260px" }}>
                        <Bar
                          data={{
                            labels: g.labels,
                            datasets: [
                              {
                                label: "Selections",
                                data: g.counts,
                                backgroundColor: "rgba(0, 123, 255, 0.6)",
                                borderColor: "rgba(0, 123, 255, 1)",
                                borderWidth: 1,
                              },
                            ],
                          }}
                          options={{ responsive: true, maintainAspectRatio: false }}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* Activity */}
                {(() => {
                  const { labels, stories, minutes } = normalizeReadingActivity(
                    selectedStudent.readingActivity || []
                  );
                  if (!labels.length) return null;
                  return (
                    <>
                      <div
                        style={{
                          marginTop: "30px",
                          backgroundColor: "#f9f9f9",
                          padding: "20px",
                          borderRadius: "12px",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                        }}
                      >
                        <h3 style={{ fontSize: "1rem", marginBottom: "10px", color: "#333" }}>
                          Stories (Last 7 days)
                        </h3>
                        <div style={{ height: "260px" }}>
                          <Bar
                            data={{
                              labels,
                              datasets: [
                                {
                                  label: "Stories",
                                  data: stories,
                                  backgroundColor: "rgba(0,123,255,0.6)",
                                  borderColor: "rgba(0,123,255,1)",
                                  borderWidth: 1,
                                },
                              ],
                            }}
                            options={{ responsive: true, maintainAspectRatio: false }}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: "30px",
                          backgroundColor: "white",
                          padding: "20px",
                          borderRadius: "12px",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                        }}
                      >
                        <h3 style={{ fontSize: "1rem", marginBottom: "10px", color: "#333" }}>
                          Reading Time (Minutes)
                        </h3>
                        <div style={{ height: "260px" }}>
                          <Bar
                            data={{
                              labels,
                              datasets: [
                                {
                                  label: "Time (min)",
                                  data: minutes,
                                  backgroundColor: "rgba(255,99,132,0.6)",
                                  borderColor: "rgba(255,99,132,1)",
                                  borderWidth: 1,
                                },
                              ],
                            }}
                            options={{ responsive: true, maintainAspectRatio: false }}
                          />
                        </div>
                      </div>
                    </>
                  );
                })()}

                <div style={{ textAlign: "center", marginTop: "30px" }}>
                  <button
                    onClick={handleCloseModal}
                    style={{ padding: "10px 20px", backgroundColor: "gray", color: "white", border: "none", borderRadius: "5px" }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </Box>
        </Modal>

        {/* Theme Editor Modal */}
        <Modal open={isThemeModalOpen} onClose={() => setIsThemeModalOpen(false)} aria-labelledby="theme-editor-title">
          <Box
            sx={{
              backgroundColor: "white",
              margin: "50px auto",
              padding: "24px",
              borderRadius: "10px",
              width: "90%",
              maxWidth: 720,
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: 24,
            }}
          >
            <h2 id="theme-editor-title" style={{ color: "#007BFF", marginBottom: 8 }}>
              {editThemeData ? "Edit Theme" : "Assign New Theme to Class"}
            </h2>
            <p style={{ marginTop: 0, color: "#555" }}>
              Assign a reading theme to one or more classes. Students will see it while creating stories.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
              <div>
                <label>Theme</label>
                <input
                  value={selectedTheme}
                  onChange={(e) => setSelectedTheme(e.target.value)}
                  placeholder="e.g. Space Adventures"
                  style={{ width: "100%", padding: 8 }}
                />
              </div>
              <div>
                <label>Setting</label>
                <input
                  value={themeSetting}
                  onChange={(e) => setThemeSetting(e.target.value)}
                  placeholder="e.g. Mars colony"
                  style={{ width: "100%", padding: 8 }}
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label>Description</label>
                <textarea
                  value={themeDescription}
                  onChange={(e) => setThemeDescription(e.target.value)}
                  placeholder="Short description of the theme"
                  style={{ width: "100%", padding: 8, minHeight: 60 }}
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label>Message (optional)</label>
                <input
                  value={themeComment}
                  onChange={(e) => setThemeComment(e.target.value)}
                  placeholder="Guidance for students (shown in the app)"
                  style={{ width: "100%", padding: 8 }}
                />
              </div>
              <div>
                <label>Expiry Date</label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  style={{ width: "100%", padding: 8 }}
                />
              </div>
              <div>
                <label>Classes</label>
                <select
                  multiple
                  value={selectedClasses}
                  onChange={(e) =>
                    setSelectedClasses(Array.from(e.target.selectedOptions).map((o) => o.value))
                  }
                  style={{ width: "100%", padding: 8, minHeight: 90 }}
                  disabled={!!editThemeData} // editing a single doc: class is fixed
                >
                  {Array.from(new Set(students.map((s) => s.className).filter(Boolean))).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {editThemeData && (
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                    Editing an existing theme for <b>{editThemeData.className || "this class"}</b>.
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              {editThemeData?.id && (
                <button
                  onClick={async () => {
                    try {
                      await deleteDoc(editThemeData.ref);
                      toast.success("🗑️ Theme deleted.");
                      setIsThemeModalOpen(false);
                      setEditThemeData(null);
                    } catch (e) {
                      console.error("Delete theme error:", e);
                      toast.error(
                        e?.code === "permission-denied"
                          ? "You don’t have permission to delete themes for this school."
                          : `Couldn't delete the theme: ${e?.message || "Unknown error"}`
                      );
                    }
                  }}
                  style={{ background: "#ef4444", color: "white", padding: "10px 14px", borderRadius: 6, border: "none" }}
                >
                  Delete
                </button>
              )}
              <button
                onClick={() => {
                  setIsThemeModalOpen(false);
                  setEditThemeData(null);
                }}
                style={{ background: "#6b7280", color: "white", padding: "10px 14px", borderRadius: 6, border: "none" }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const base = {
                      theme: selectedTheme?.trim() || "",
                      setting: themeSetting?.trim() || "",
                      description: themeDescription?.trim() || "",
                      comment: themeComment?.trim() || "",
                      classes: editThemeData
                        ? (editThemeData.classes || [editThemeData.className].filter(Boolean))
                        : selectedClasses,
                      className: editThemeData ? editThemeData.className : null, // will set per-class below
                      schoolId,
                      // ✅ server timestamps for stability
                      createdAt: editThemeData
                        ? (editThemeData.createdAt || serverTimestamp())
                        : serverTimestamp(),
                      expiryDate: expiryDate ? new Date(expiryDate).toISOString() : null,
                    };

                    if (!base.theme) {
                      toast.error("Please enter a theme name.");
                      return;
                    }

                    if (editThemeData) {
                      // Update the single existing doc
                      const write = {
                        ...base,
                        className: editThemeData.className || base.className || null,
                        classes: Array.isArray(base.classes) && base.classes.length
                          ? base.classes
                          : [editThemeData.className].filter(Boolean),
                        updatedAt: serverTimestamp(), // ✅
                      };
                      await setDoc(editThemeData.ref, write, { merge: true });
                      toast.success("✏️ Theme updated successfully!");
                    } else {
                      // Create one doc per selected class under schools/{sid}/classrooms/{class}/themes
                      const targets = Array.isArray(selectedClasses) && selectedClasses.length
                        ? selectedClasses
                        : [];
                      if (!targets.length) {
                        toast.error("Please select at least one class.");
                        return;
                      }
                      const ops = targets.map((cls) =>
                        addDoc(
                          collection(db, "schools", schoolId, "classrooms", cls, "themes"),
                          {
                            ...base,
                            classes: targets,
                            className: cls,
                          }
                        )
                      );
                      await Promise.all(ops);
                      toast.success("✅ Theme(s) saved successfully!");
                    }

                    // reset & close
                    setIsThemeModalOpen(false);
                    setEditThemeData(null);
                    setSelectedTheme("");
                    setThemeSetting("");
                    setThemeDescription("");
                    setThemeComment("");
                    setExpiryDate("");
                    setSelectedClasses([]);
                  } catch (e) {
                    console.error("Save theme error:", e);
                    toast.error(
                      e?.code === "permission-denied"
                        ? "You don’t have permission to save themes for this school. Check your user.schoolId & role."
                        : `Save failed: ${e?.message || "Unknown error"}`
                    );
                  }
                }}
                // ✅ Guard button to avoid avoidable denials
                disabled={
                  !canAdmin ||
                  !schoolId ||
                  !selectedTheme?.trim() ||
                  (!editThemeData && !selectedClasses.length)
                }
                style={{
                  background: !canAdmin || !schoolId ? "#94a3b8" : "#10b981",
                  color: "white",
                  padding: "10px 14px",
                  borderRadius: 6,
                  border: "none",
                  cursor: (!canAdmin || !schoolId) ? "not-allowed" : "pointer"
                }}
              >
                {editThemeData ? "Save Changes" : "Save Theme"}
              </button>
            </div>
          </Box>
        </Modal>

        <ToastContainer position="top-right" autoClose={3000} />
      </div>

      <footer className="page-footer">
        <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
        <nav style={{ marginTop: "10px" }}>
          <a href="/about" className="footer-link">About Us</a>
          <a href="/contact" className="footer-link">Contact</a>
        </nav>
      </footer>
    </div>
  );
};

export default SchoolDashboard;

